import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeUnit, familyUnits } from '../utils/units';
import { recipeLineCost } from '../utils/recipeCost';
import { formatCurrency } from '../utils/formatCurrency';
import './ItemForm.css';

/**
 * Editor de líneas de receta (insumo / cantidad / unidad / crítico).
 *
 * Nació dentro de `ItemForm` (RES-17/18/30) y se extrajo para reutilizarlo en las recetas
 * estándar de la organización (RES-45): el mismo editor arma la receta de un producto del local
 * y la estándar que baja a todas las sucursales. Con `readOnly` solo lista las líneas (receta
 * bloqueada por la organización o reemplazada por la estándar).
 *
 * @param {Object} props
 * @param {Array} props.lines - Líneas `{ inventoryItemId, quantity, unit, name?, isCritical? }`
 * @param {Function} props.onChange - Recibe las líneas nuevas
 * @param {Array} props.inventoryItems - Insumos elegibles (`itemId, name, unit, cost?, avgCost?`)
 * @param {boolean} [props.readOnly] - Sin agregar/quitar/tildar
 * @param {string} [props.idPrefix] - Prefijo de los ids de los campos (`recipe` en ItemForm)
 */
const OrgRecipeLinesEditor = ({
  lines,
  onChange,
  inventoryItems = [],
  readOnly = false,
  idPrefix = 'recipe',
}) => {
  const { t } = useTranslation();
  const [selectedIngredientId, setSelectedIngredientId] = useState('');
  const [ingredientQty, setIngredientQty] = useState('');
  const [ingredientUnit, setIngredientUnit] = useState('');

  const recipe = Array.isArray(lines) ? lines : [];

  const handleSelectIngredient = (e) => {
    const id = e.target.value;
    setSelectedIngredientId(id);
    const item = inventoryItems.find((i) => i.itemId === id);
    // Propone la unidad del insumo; el selector permite las otras de la misma familia (RES-17)
    setIngredientUnit(item ? normalizeUnit(item.unit) || 'un' : '');
  };

  const addIngredient = () => {
    if (!selectedIngredientId || !ingredientQty) return;
    const invItem = inventoryItems.find((i) => i.itemId === selectedIngredientId);
    if (!invItem) return;
    const line = {
      inventoryItemId: selectedIngredientId,
      quantity: parseFloat(ingredientQty),
      unit: ingredientUnit || normalizeUnit(invItem.unit) || 'un',
    };
    onChange([
      ...recipe,
      {
        ...line,
        name: invItem.name,
        // Snapshot del costo al momento de armar la receta; en pantalla se muestra el actual (RES-18)
        cost: recipeLineCost(line, inventoryItems),
      },
    ]);
    setSelectedIngredientId('');
    setIngredientQty('');
    setIngredientUnit('');
  };

  const removeIngredient = (index) => {
    onChange(recipe.filter((_, idx) => idx !== index));
  };

  // Ingrediente crítico (RES-30): sin él el producto sale de la carta.
  const toggleIngredientCritical = (index) => {
    onChange(
      recipe.map((line, idx) => (idx === index ? { ...line, isCritical: !line.isCritical } : line))
    );
  };

  const lineName = (line) =>
    line.name ||
    inventoryItems.find((i) => i.itemId === line.inventoryItemId)?.name ||
    line.inventoryItemId;

  return (
    <div className="org-recipe-lines-editor">
      {!readOnly && (
        <div className="recipe-form-row">
          <div className="recipe-form-field flex-2">
            <label htmlFor={`${idPrefix}-ingredient`}>{t('menu.recipe.ingredientLabel')}</label>
            <select
              id={`${idPrefix}-ingredient`}
              value={selectedIngredientId}
              onChange={handleSelectIngredient}
            >
              <option value="">{t('menu.recipe.selectIngredient')}</option>
              {inventoryItems.map((item) => (
                <option key={item.itemId} value={item.itemId}>
                  {item.name} ({normalizeUnit(item.unit) || item.unit})
                  {Number(item.cost) > 0 &&
                    ` · ${formatCurrency(item.cost)}/${normalizeUnit(item.unit) || item.unit}`}
                </option>
              ))}
            </select>
          </div>
          <div className="recipe-form-field flex-1">
            <label htmlFor={`${idPrefix}-qty`}>{t('menu.recipe.quantity')}</label>
            <input
              id={`${idPrefix}-qty`}
              type="number"
              step="0.01"
              value={ingredientQty}
              onChange={(e) => setIngredientQty(e.target.value)}
            />
          </div>
          <div className="recipe-form-field flex-1">
            <label htmlFor={`${idPrefix}-unit`}>{t('menu.recipe.unit')}</label>
            <select
              id={`${idPrefix}-unit`}
              value={ingredientUnit}
              onChange={(e) => setIngredientUnit(e.target.value)}
              disabled={!selectedIngredientId}
            >
              {(ingredientUnit ? familyUnits(ingredientUnit) : ['']).map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={addIngredient}
            className="btn btn-sm btn-secondary"
            disabled={!selectedIngredientId || !ingredientQty}
            aria-label={t('menu.addIngredient')}
          >
            +
          </button>
        </div>
      )}

      {recipe.length > 0 ? (
        <ul className="list-group">
          {recipe.map((ing, idx) => (
            <li key={idx} className="list-group-item">
              <span>
                {lineName(ing)} — {ing.quantity} {ing.unit}
                {recipeLineCost(ing, inventoryItems) > 0 && (
                  <span className="text-muted ml-2">
                    ({formatCurrency(recipeLineCost(ing, inventoryItems))})
                  </span>
                )}
                {ing.isCritical && (
                  <span className="recipe-critical-badge">{t('menu.recipe.criticalBadge')}</span>
                )}
              </span>
              {!readOnly && (
                <>
                  <label className="recipe-critical-toggle">
                    <input
                      type="checkbox"
                      checked={ing.isCritical === true}
                      onChange={() => toggleIngredientCritical(idx)}
                    />
                    {t('menu.recipe.criticalLabel')}
                  </label>
                  <button
                    type="button"
                    onClick={() => removeIngredient(idx)}
                    className="btn btn-sm btn-danger"
                    aria-label={t('menu.recipe.removeIngredient')}
                  >
                    x
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted small">{t('menu.recipe.empty')}</p>
      )}
    </div>
  );
};

export default OrgRecipeLinesEditor;
